import { browser, dev } from '$app/env';
import { afterNavigate, goto } from '$app/navigation';
import { navigating, page } from '$app/state';
import { tick } from 'svelte';
import { sessionId } from '~/auth/utils.svelte';
import { useIdleCallback } from '~/lib/idle.svelte';

type Action = 'init' | 'push' | 'pop' | 'replace' | 'link' | 'goto' | 'exit' | 'idle' | 'active';

type NavHistory = {
  action: Action;
  url: string;
  state: App.PageState;
  timestamp: Date;
};

export const navHistory = $state<{ current: NavHistory[] }>({
  current: [],
});

const pushNavHistory = (
  action: Action,
  url: string | URL,
  state: App.PageState,
) => {
  navHistory.current.push({
    action,
    state,
    url: url.toString(),
    timestamp: new Date(),
  });
};

const trimParams = (search: URLSearchParams) => {
  return [...search].length ? '?' + search.toString().replace(/^\?/, '') : '';
};

const combineState = (
  state: App.PageState,
  key: string,
  val?: string | boolean | null,
) => {
  return { ...state, [key]: val ?? true };
};

const omitState = (state: App.PageState, key?: string) => {
  const copy = { ...state };
  if (key) {
    delete copy[key];
  }
  return copy;
};

// Shallow routing leaves page.url on the underlying page and exposes the
// shallow entry's URL (which carries the modal query params) as
// page.shallow.url.
const currentUrl = () => page.shallow?.url ?? page.url;

// page.url is readonly, so build navigation targets from a copy
const copyUrl = () => new URL(currentUrl().href);

// include the pathname: a bare search string resolves against the document URL
const toPath = (url: URL, hash = '') =>
  url.pathname + trimParams(url.searchParams) + hash;

type ModalOptions = {
  // CSS z-index the modal renders at. A push is inserted beneath open modals
  // from higher layers. Omitted means topmost, which keeps the old behavior.
  layer?: number;
};

// keyed by modal key since some keys have an instance per game mode
const modalLayers = new Map<string, { layer: number; query: boolean }>();

const isModalOpen = (key: string, query: boolean) =>
  query ? currentUrl().searchParams.has(key) : Boolean(page.state[key]);

/**
 * Pushing a modal onto the history stack while modals from a higher layer are
 * open puts it on top of history but beneath them on screen, so back closes
 * the wrong one (e.g. game over resuming under an open premium modal).
 * Instead, rebuild the top of the stack as [..., base + self, base + self +
 * above]. Query modals opened straight from the URL have no entry of their
 * own, so they're stripped from the current entry and re-pushed. If several
 * above modals own entries, they collapse into one.
 * Resolves false when nothing is above and a normal push should happen.
 */
const pushBeneath = async (
  self: { key: string; layer: number; query: boolean },
  val?: string | null,
  replace?: string,
  hash = '',
) => {
  const above = [...modalLayers].filter(
    ([key, m]) =>
      key !== self.key && m.layer > self.layer && isModalOpen(key, m.query),
  );
  if (!above.length) return false;

  const url = copyUrl();
  const base = omitState(page.state, replace);
  const owned = above.some(([key]) => key in page.state);
  const aboveState: App.PageState = {};
  const aboveParams: [string, string][] = [];
  for (const [key, m] of above) {
    if (m.query) {
      const value = url.searchParams.get(key)!;
      aboveParams.push([key, value]);
      url.searchParams.delete(key);
      aboveState[key] = base[key] ?? (value === 't' ? true : value);
    } else {
      aboveState[key] = base[key];
    }
    delete base[key];
  }

  // shallow goto is async in SvelteKit 3, so each step is awaited in order
  if (!owned) {
    pushNavHistory('replace', url, base);
    await goto(toPath(url), { shallow: true, replace: true, state: base });
  }
  if (self.query) {
    url.searchParams.set(self.key, val ?? 't');
  }
  const selfState = combineState(base, self.key, val);
  pushNavHistory(owned ? 'replace' : 'push', url, selfState);
  await goto(toPath(url, hash), { shallow: true, replace: owned, state: selfState });

  for (const [key, value] of aboveParams) {
    url.searchParams.set(key, value);
  }
  const topState = { ...selfState, ...aboveState };
  pushNavHistory('push', url, topState);
  await goto(toPath(url), { shallow: true, state: topState });
  return true;
};

export class ModalState<T extends string> {
  key: T;
  layer: number;
  query = false;
  constructor(key: T, options: ModalOptions = {}) {
    this.key = key;
    this.layer = options.layer ?? Infinity;
    modalLayers.set(key, { layer: this.layer, query: this.query });
  }

  async push(val?: string | null, replace?: string) {
    await tick();
    if (!page.state[this.key]) {
      if (await pushBeneath(this, val, replace)) {
        await tick();
        return;
      }
      const url = copyUrl();
      const newState = combineState(omitState(page.state, replace), this.key, val);
      pushNavHistory('push', url, newState);
      await goto(toPath(url), { shallow: true, state: newState });
      await tick();
    }
  }
  async pop(override = false) {
    await tick();
    if (this.key in page.state || override) {
      history.back();
      await tick();
    }
  }

  async replace(val?: string, replace?: string) {
    await tick();
    const url = copyUrl();
    const newState = combineState(omitState(page.state, replace), this.key, val);
    pushNavHistory('replace', url, newState);
    await goto(toPath(url), { shallow: true, replace: true, state: newState });
    await tick();
  }

  get open() {
    return Boolean(page.state[this.key]);
  }
  get value() {
    return page.state[this.key] as T;
  }
}

export class ModalQueryState<T extends string> {
  key: T;
  layer: number;
  query = true;
  constructor(key: T, options: ModalOptions = {}) {
    this.key = key;
    this.layer = options.layer ?? Infinity;
    modalLayers.set(key, { layer: this.layer, query: this.query });
  }

  async push(val?: string, replace?: string) {
    await tick();
    const url = copyUrl();
    let hash = '';
    if (val) {
      [val, hash] = val.split('#');
      hash = hash ? `#${hash}` : '';
    }
    if (url.searchParams.has(this.key)) {
      // avoid double pushing
      return;
    }
    if (await pushBeneath(this, val, replace, hash)) {
      await tick();
      return;
    }
    url.searchParams.set(this.key, val ?? 't');
    const newState = combineState(omitState(page.state, replace), this.key, val);
    pushNavHistory('push', url, newState);
    await goto(toPath(url, hash), { shallow: true, state: newState });
    await tick();
  }

  async pop(override = false) {
    await tick();
    const url = copyUrl();
    console.log(url.searchParams, navHistory.current);
    if (url.searchParams.has(this.key) || override) {
      if (navHistory.current.length === 0) {
        pushNavHistory('replace', url, {});
        await goto(url.pathname, { replace: true });
      } else {
        history.back();
      }
      await tick();
    }
  }
  async replace(val?: string, replace?: string) {
    await tick();
    const url = copyUrl();
    url.searchParams.set(this.key, val ?? 't');
    const newState = combineState(omitState(page.state, replace), this.key, val);
    pushNavHistory('replace', url, newState);
    await goto(toPath(url), { shallow: true, replace: true, state: newState });
    await tick();
  }

  open = $derived.by(() => browser && currentUrl().searchParams.has(this.key));
  get value() {
    return browser ? currentUrl().searchParams.get(this.key) : undefined;
  }
}

if (browser) {
  // page.url is a placeholder ('a:') until the router initializes, which
  // happens after this module loads, so read the landing URL (incl. ?ref=)
  // straight from location.
  pushNavHistory('init', location.href, {});

  // TODO try to detect if browser or user initiated
  window.addEventListener('popstate', function (e) {
    if (e.state !== null) {
      pushNavHistory('pop', location.href, page.state);
    }
  });
}

// Must be called during component initialisation (not inside onMount).
// Captures full-page navigations that shallow routing and popstate miss.
export const initNavTracking = () => {
  afterNavigate((nav) => {
    if (nav.shallow) return;

    // 'enter' is covered by the init push at module load.
    // 'popstate' is covered by the window popstate listener.
    if (nav.type !== 'link' && nav.type !== 'goto') return;
    const to = nav.to;
    if (to) {
      pushNavHistory(nav.type, to.url, page.state);
    }
  });
};

// Number of navHistory entries already sent; the server appends each flush to the session's stored history.
let flushedCount = 0;

const flushNavHistory = (addExit: boolean) => {
  if (dev) return;
  const history: Partial<NavHistory>[] = navHistory.current.slice(flushedCount);
  if (addExit) history.push({ action: 'exit', timestamp: new Date() });
  if (!history.length) return;
  const sent = navigator.sendBeacon(
    '/api/analytics/nav-history',
    JSON.stringify({ history, version: localStorage.getItem('version'), session_id: sessionId.current }),
  );
  if (sent) flushedCount = navHistory.current.length;
};

// Must be called during component initialisation (not inside onMount).
export const initIdleNavTracking = () => {
  useIdleCallback(
    () => {
      navHistory.current.push({
        action: 'idle',
        url: page.url.toString(),
        state: page.state,
        timestamp: new Date(),
      });
      flushNavHistory(false);
    },
    60_000,
    () => {
      navHistory.current.push({
        action: 'active',
        url: page.url.toString(),
        state: page.state,
        timestamp: new Date(),
      });
    },
  );
};

export const useUnload = async () => {
  if (!browser) return;
  window.onbeforeunload = () => flushNavHistory(true);
};

export const navigatingPage = {
  get current() {
    return navigating.from !== null && navigating.from.route.id !== navigating.to?.route.id;
  }
};
